import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    Input,
    OnInit,
    QueryList,
    ViewChildren
} from '@angular/core';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { OffClickModule } from '@df/module-off-click/off-click.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { DfUiModule } from '@df/ui/df-ui.module';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsContent } from '@icms/core/content';
import { ICmsContentComponentValidationService } from '@icms/core/content/component/icms-content-component-validation.service';
import { NPageSearch } from '../../../search/page-search.interfaces';
import { NPageHeader } from '../../page-header.component';

@Component({
    standalone: true,
    imports: [CommonModule, OffClickModule, ToggleModule, ICmsClientModule, DfUiModule, HrefComponent],
    selector: 'page-header-meganav-menu',
    templateUrl: 'page-header-meganav-menu.html',
    styleUrl: 'page-header-meganav-menu.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageHeaderMeganavMenuComponent extends UiComponent implements OnInit, AfterViewInit {
    /**
     * Meganav items
     */
    @Input()
    @ICmsContent({
        watchDepth: 0
    })
    items?: NPageHeader.IMeganavItem[];

    @ViewChildren('link')
    linksElementRefs?: QueryList<ElementRef>;

    elementsWidths: number[] | undefined;

    private readonly toggleService = inject(ToggleService);
    private readonly iCmsContentComponentValidationService = inject(ICmsContentComponentValidationService);
    private readonly app = inject(App);
    private readonly meta = inject(Meta);
    /**
     * Group id for toggle service
     */
    readonly toggleGroupId = NPageHeader.MEGANAV_GROUP_ID;

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.meta.subscribe(() => this.toggleService.closeGroup(this.toggleGroupId)));
    }

    ngAfterViewInit(): void {
        this.setElementsWidths();
        this.subscriptions.add(this.linksElementRefs?.changes.subscribe(() => this.setElementsWidths()));
    }

    onMouseIn(index: number): void {
        if (!this.client.isCms && this.items && this.hasSubmenu(this.items[index])) {
            this.toggleService.open([this.toggleGroupId, index]);
        }
    }

    onMouseOut(index: number): void {
        if (!this.client.isCms) {
            this.toggleService.close([this.toggleGroupId, index], { delay: 300 });
        }
    }

    onClick(index: number): void {
        if (this.client.isCms) {
            this.toggleService.toggle([this.toggleGroupId, index]);
        }
    }

    closeMeganav() {
        this.toggleService.closeGroup(NPageHeader.MEGANAV_GROUP_ID, { exceptionItems: [NPageSearch.SEARCH_MEGANAV_KEY] });
    }

    /*
     * in icms all can have submenu, outside only when its set and its valid
     */
    private hasSubmenu(item: NPageHeader.IMeganavItem): boolean {
        return !!item.columns.length && !this.iCmsContentComponentValidationService.getState$(item, undefined).getValue();
    }

    getWidth(index: number): number {
        return this.elementsWidths?.[index] || 0;
    }

    setElementsWidths(): void {
        if (this.app.isPlatformBrowser) {
            if (this.linksElementRefs?.length) {
                this.elementsWidths = this.linksElementRefs?.map(el => el.nativeElement.getBoundingClientRect().width);
                this.detectChanges();
            }
        }
    }
}
